import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AudioLines,
  BellRing,
  BookCopy,
  BookOpen,
  BookPlus,
  BookText,
  Building2,
  CalendarCheck,
  CalendarDays,
  ChartNoAxesColumnIncreasing,
  ChartNoAxesCombined,
  CircleHelp,
  Clapperboard,
  ClipboardCheck,
  CreditCard,
  Database,
  FilePen,
  FlaskConical,
  GraduationCap,
  Grid2X2,
  House,
  Languages,
  LayoutDashboard,
  LibraryBig,
  LifeBuoy,
  Lightbulb,
  ListTree,
  Lock,
  Magnet,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  MonitorPlay,
  Network,
  Package,
  PlayCircle,
  Presentation,
  Puzzle,
  ReceiptText,
  School,
  Settings,
  ShieldCheck,
  Sigma,
  Ticket,
  TicketPercent,
  Tickets,
  TrendingUp,
  UserRoundCheck,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';
import { useTheme } from '@context/ThemeContext';
import { useAuth } from '@context/AuthContext';
import { HelpChatbot } from '@components/ui/HelpChatbot';
import { SupportTicketModal } from '@components/ui/SupportTicketModal';
import { useEntitlements } from '@context/EntitlementContext';
import { routeFeature } from '@/config/featureRegistry';

interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  route: string;
  badge?: number;
  roles?: string[];  // if set, only render for these roles; undefined = all roles in the section
}

interface MenuSection {
  label: string;
  roles?: string[];  // if set, only render for these roles; undefined = all roles
  items: MenuItem[];
}

// Role helpers
function _hasRole(user: any, ...roles: string[]): boolean {
  const userRoles: string[] = (user?.roles ?? (user?.role ? [user.role] : [])).map((r: string) => r.toLowerCase());
  return roles.some(r => userRoles.includes(r.toLowerCase()));
}

// Roles that get the teacher-facing Teach/Manage/Language Learning navigation. Special Educator and
// School Counselor are teacher-equivalent for navigation; they differ only by holding the
// "Course Prompt Settings" permission (see types/permissions.ts).
const TEACHER_TIER_ROLES = ['teacher', 'special educator', 'school counselor', 'admin', 'super admin'];

// A parent account gets ONLY this navigation: a read-only view of their linked children. None of the learner or
// teacher sections apply to them.
const PARENT_SECTIONS: MenuSection[] = [
  {
    label: 'My child',
    items: [
      { id: 'parent-home',        label: 'Home',              icon: House, route: '/parent' },
      { id: 'parent-progress',    label: 'Progress',          icon: TrendingUp, route: '/parent/progress' },
      { id: 'parent-assessments', label: 'Assessments',       icon: ClipboardCheck, route: '/parent/assessments' },
      { id: 'parent-activity',    label: 'Learning Activity', icon: CalendarDays, route: '/parent/activity' },
      { id: 'parent-insights',    label: 'AI Insights',       icon: Lightbulb, route: '/parent/insights' },
    ],
  },
  {
    label: 'Account',
    items: [
      { id: 'parent-payments',    label: 'Payments',          icon: CreditCard, route: '/parent/payments' },
    ],
  },
];

// Full nav definition — sections and items tagged with required roles.
// Grouping/labels follow the requested nav spec. Two live routes it didn't mention
// (calendar-management, replay-academy-management) are kept under their closest-fit new
// section rather than dropped; Mentor Dashboard (a mentor's own landing page, distinct from
// the admin-facing mentoring tools below) keeps its own section for the same reason.
const ALL_SECTIONS: MenuSection[] = [
  {
    // Core scheduled/self-paced learning.
    label: 'Learn',
    items: [
      { id: 'dashboard',      label: 'Dashboard',      icon: LayoutDashboard, route: '/dashboard' },
      { id: 'calendar',       label: 'Calendar',       icon: CalendarDays, route: '/calendar' },
      { id: 'my-learnings',   label: 'My Learnings',   icon: BookOpen, route: '/my-learnings' },
      { id: 'language-learning', label: 'Language Learning', icon: Languages, route: '/language-learning' },
      { id: 'join-class',     label: 'Join Class',     icon: GraduationCap, route: '/join-class' },
    ],
  },
  {
    // Live/async Q&A with a teacher, as opposed to self-paced Learn content.
    label: 'Interaction',
    items: [
      { id: 'doubt-session',  label: 'Doubt Session',  icon: MessagesSquare, route: '/doubt-session' },
      { id: 'ask-question',   label: 'Ask Question',   icon: CircleHelp, route: '/ask-question' },
    ],
  },
  {
    label: 'Replay',
    items: [
      { id: 'replay-academy', label: 'Replay Academy', icon: PlayCircle, route: '/replay-academy' },
    ],
  },
  {
    label: 'Account',
    items: [
      { id: 'subscription',   label: 'Subscription',   icon: CreditCard, route: '/subscription' },
      { id: 'payment-history', label: 'Payment History', icon: ReceiptText, route: '/payment-history' },
    ],
  },
  {
    label: 'Support',
    items: [
      { id: 'tickets',        label: 'My Tickets',     icon: Tickets, route: '/tickets' },
      { id: 'ticket-dashboard', label: 'Ticket Dashboard', icon: Ticket, route: '/tickets/dashboard', roles: ['support', 'admin', 'super admin'] },
      // Opens the HelpChatbot modal (see handleNav's special-case below) -- not a real route.
      { id: 'help-docs',      label: 'Help & Documentation', icon: BookText, route: '/help' },
    ],
  },
  {
    label: 'Teach',
    roles: TEACHER_TIER_ROLES,
    items: [
      { id: 'my-teaching',  label: 'My Teaching',  icon: Presentation, route: '/my-teaching' },
      { id: 'my-classes',   label: 'My Classes',   icon: LibraryBig, route: '/my-classes' },
      { id: 'calendar-management', label: 'Calendar', icon: CalendarCheck, route: '/calendar-management' },
    ],
  },
  {
    label: 'Content',
    roles: TEACHER_TIER_ROLES,
    items: [
      { id: 'courses',           label: 'Courses',            icon: BookCopy, route: '/courses' },
      { id: 'course-management', label: 'Course Builder',     icon: BookPlus, route: '/course-management' },
      { id: 'test-management',   label: 'Test',    icon: ClipboardCheck, route: '/test-management' },
      { id: 'replay-academy-management', label: 'Replay Academy', icon: Video, route: '/replay-academy-management' },
      { id: 'knowledge-capsule-management', label: 'Knowledge Capsules', icon: Network, route: '/knowledge-capsules' },
      { id: 'll-category-matrix', label: 'Category Matrix', icon: Grid2X2, route: '/language-learning/instructor/matrix' },
      { id: 'll-content-structure', label: 'Content Structure', icon: ListTree, route: '/language-learning/instructor/structure' },
    ],
  },
  {
    label: 'School',
    roles: ['school admin', 'admin', 'super admin'],
    items: [
      { id: 'school-console', label: 'School Console', icon: School, route: '/school/overview' },
      // Managing the school list itself stays admin-only -- narrower than the section's role gate.
      { id: 'school-management', label: 'Schools',           icon: Building2, route: '/admin/schools', roles: ['admin', 'super admin'] },
    ],
  },
  {
    label: 'Users & Access',
    // Widened to also admit 'mentor' for Mentor Dashboard below -- each item still carries its
    // own (narrower) roles, so this only changes grouping, not who can see what.
    roles: [...TEACHER_TIER_ROLES, 'mentor'],
    items: [
      { id: 'users',             label: 'Users',              icon: Users, route: '/users' },
      { id: 'roles-permissions', label: 'Roles & Permissions', icon: ShieldCheck, route: '/roles-permissions' },
      // Both admin-only -- narrower than the section's role gate (shared with Users/Roles above).
      { id: 'mentor-action-item-management', label: 'Mentoring Checklist', icon: ClipboardCheck, route: '/admin/mentor-action-items', roles: ['admin', 'super admin'] },
      { id: 'mentor-assignment-management', label: 'Mentor Assignments', icon: UserRoundCheck, route: '/admin/mentor-assignments', roles: ['admin', 'super admin'] },
      // A mentor's own landing page -- grouped here with the admin-facing mentoring tools above.
      { id: 'mentor-dashboard', label: 'Mentor Dashboard', icon: ChartNoAxesCombined, route: '/mentor-dashboard', roles: ['mentor', 'admin', 'super admin'] },
    ],
  },
  {
    label: 'Platform',
    roles: ['admin', 'super admin'],
    items: [
      { id: 'feature-management', label: 'Feature Management', icon: Puzzle, route: '/admin/features' },
      { id: 'plan-management',   label: 'Plan Management',   icon: Package, route: '/admin/plans' },
      { id: 'coupon-management', label: 'Coupon Management', icon: TicketPercent, route: '/admin/coupons' },
      { id: 'revenue-dashboard', label: 'Revenue Dashboard', icon: ChartNoAxesCombined, route: '/admin/revenue' },
      { id: 'lead-management', label: 'Leads', icon: Magnet, route: '/admin/leads' },
      { id: 'notification-platform', label: 'Notifications & Jobs', icon: BellRing, route: '/admin/notifications' },
      { id: 'whatsapp-execution-engine', label: 'WhatsApp Broadcasts', icon: MessageCircle, route: '/admin/whatsapp-broadcasts' },
    ],
  },
  {
    label: 'Engagement',
    roles: TEACHER_TIER_ROLES,
    items: [
      { id: 'demo-request-management', label: 'Live Demo Requests', icon: MonitorPlay, route: '/admin/demo-requests' },
      // Admin-only -- narrower than the section's role gate (shared with Live Demo Requests above).
      { id: 'social-media-management', label: 'Social Media Ads', icon: Megaphone, route: '/admin/social-media', roles: ['admin', 'super admin'] },
      { id: 'video-studio', label: 'Video Studio', icon: Clapperboard, route: '/admin/video-studio', roles: ['admin', 'super admin'] },
      { id: 'youtube-growth', label: 'YouTube Growth', icon: ChartNoAxesColumnIncreasing, route: '/admin/youtube-growth', roles: ['admin', 'super admin'] },
    ],
  },
  {
    label: 'System',
    roles: TEACHER_TIER_ROLES,
    items: [
      { id: 'settings',          label: 'Settings',           icon: Settings, route: '/settings' },
      { id: 'prompts',           label: 'Prompts',            icon: FilePen, route: '/prompts' },
    ],
  },
  {
    label: 'Developer Tools',
    roles: ['admin', 'super admin'],
    items: [
      { id: 'schema-test',  label: 'Schema Test',  icon: Database, route: '/schema-test' },
      { id: 'latex-tester', label: 'LaTeX Tester', icon: Sigma,  route: '/latex-tester' },
      { id: 'lab',          label: 'Lab',          icon: FlaskConical, route: '/lab' },
      { id: 'live-voice',   label: 'Live Voice',   icon: AudioLines, route: '/live-voice' },
    ],
  },
];

export const Sidebar: React.FC<{ isOpen?: boolean; onToggle?: (v: boolean) => void }> = ({
  isOpen = false,
  onToggle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeConfig } = useTheme();
  const { user } = useAuth();
  const { has, isComingSoon } = useEntitlements();
  const [menuOpen, setMenuOpen] = useState(isOpen);
  const [collapsed, setCollapsed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [escalateContext, setEscalateContext] = useState<{ sessionId?: string; message?: string }>({});

  const isTeacher = _hasRole(user, ...TEACHER_TIER_ROLES);
  const isStudent = !isTeacher;

  // Filter sections and items based on user role
  const MENU_SECTIONS = useMemo(() => {
    if (_hasRole(user, 'parent')) return PARENT_SECTIONS;
    return ALL_SECTIONS
      .filter(section => !section.roles || _hasRole(user, ...section.roles))
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          // Join Class: only shown for students, regardless of which section it lives in
          if (item.id === 'join-class' && !isStudent) return false;
          if (item.roles && !_hasRole(user, ...item.roles)) return false;
          return true;
        }),
      }))
      .filter(section => section.items.length > 0);
  }, [user, isStudent]);

  const handleNav = (item: MenuItem) => {
    // "Help & Documentation" opens the help modal in place rather than navigating.
    if (item.id === 'help-docs') {
      setHelpOpen(true);
      if (typeof window !== 'undefined' && window.innerWidth < 768 && menuOpen) {
        setMenuOpen(false);
        onToggle?.(false);
      }
      return;
    }
    navigate(item.route);
    if (typeof window !== 'undefined' && window.innerWidth < 768 && menuOpen) {
      setMenuOpen(false);
      onToggle?.(false);
    }
  };

  const toggleMobile = () => {
    const next = !menuOpen;
    setMenuOpen(next);
    onToggle?.(next);
  };

  return (
    <>
      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={toggleMobile}
        className={`fixed top-3.5 left-4 z-50 md:hidden p-2 rounded-lg
                    ${themeConfig.accent.primary} text-white shadow-lg transition-colors`}
        aria-label="Toggle menu"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
            d={menuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
        </svg>
      </button>

      {/* Mobile overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={toggleMobile} />
      )}

      {/* Sidebar panel */}
      <aside
        className={`
          fixed left-0 top-0 h-screen z-40 flex flex-col
          transform transition-all duration-300 overflow-hidden
          ${themeConfig.sidebar.background}
          border-r ${themeConfig.sidebar.border}
          ${menuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:static md:h-auto md:min-h-screen
          ${collapsed ? 'md:w-[60px]' : 'w-64 md:w-64'}
        `}
        style={{ boxShadow: '1px 0 0 rgba(255,255,255,0.04)' }}
      >
        {/* Top bar — collapse toggle only (logo lives in Header) */}
        <div className={`flex items-center h-14 px-3 border-b ${themeConfig.sidebar.border} flex-shrink-0`}>
          {!collapsed && (
            <span className={`flex-1 text-xs font-semibold tracking-widest uppercase ${themeConfig.sidebar.sectionLabel} pl-1`}>
              Navigation
            </span>
          )}
          <button
            type="button"
            onClick={() => setCollapsed(v => !v)}
            className={`hidden md:flex items-center justify-center w-8 h-8 rounded-lg
                        transition-colors ${themeConfig.sidebar.hoverBg} ${themeConfig.sidebar.text}`}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d={collapsed ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'} />
            </svg>
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4"
             style={{ scrollbarWidth: 'none' }}>
          {MENU_SECTIONS.map(section => (
            <div key={section.label}>
              {/* Section label */}
              {!collapsed && (
                <p className={`text-[9px] font-bold tracking-[0.18em] uppercase px-3 mb-1.5
                               ${themeConfig.sidebar.sectionLabel}`}>
                  {section.label}
                </p>
              )}

              <ul className="space-y-0.5">
                {section.items.map(item => {
                  const isActive = location.pathname === item.route;
                  // Locked items stay visible (the page itself explains the upgrade) -- just marked.
                  const itemFeature = routeFeature(item.route);
                  const soon = !!itemFeature && isComingSoon(itemFeature);
                  const locked = !!itemFeature && !has(itemFeature);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => handleNav(item)}
                        title={collapsed ? item.label : undefined}
                        className={`
                          w-full flex items-center gap-3 rounded-lg text-sm font-medium
                          transition-all duration-150
                          ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'}
                          ${isActive
                            ? 'bg-amber-500/10 text-[#F5C542] shadow-[inset_3px_0_0_#f59e0b]'
                            : `${themeConfig.sidebar.text} ${themeConfig.sidebar.hoverBg} ${themeConfig.sidebar.hoverText}`
                          }
                        `}
                      >
                        {/* fixed-width column so labels line up; the icon takes the row's text colour (gold when active) */}
                        <span className="flex-shrink-0 w-[18px] flex items-center justify-center">
                          <item.icon size={18} aria-hidden="true" />
                        </span>
                        {!collapsed && (
                          <span className={`flex-1 text-left truncate ${locked ? 'opacity-60' : ''}`}>{item.label}</span>
                        )}
                        {!collapsed && soon && (
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-300 flex-shrink-0">Soon</span>
                        )}
                        {!collapsed && locked && !soon && (
                          <span className="flex-shrink-0 opacity-70" title="Upgrade to unlock" aria-label="Locked"><Lock size={12} aria-hidden="true" /></span>
                        )}
                        {!collapsed && item.badge && (
                          <span className={`${themeConfig.badge.background} ${themeConfig.badge.text}
                                           text-[10px] font-bold rounded-full w-4 h-4
                                           flex items-center justify-center flex-shrink-0`}>
                            {item.badge}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer -- "Help & Documentation" moved into the Support nav section above */}
        {!collapsed && (
          <div className={`flex-shrink-0 p-3 border-t ${themeConfig.sidebar.border} space-y-1`}>
            <button
              type="button"
              onClick={() => { setEscalateContext({}); setSupportOpen(true); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs
                          transition-colors ${themeConfig.sidebar.textSecondary}
                          ${themeConfig.sidebar.hoverBg} ${themeConfig.sidebar.hoverText}`}
            >
              <span className="flex-shrink-0 w-[18px] flex items-center justify-center"><LifeBuoy size={18} aria-hidden="true" /></span>
              <span>Support Ticket</span>
            </button>
          </div>
        )}
      </aside>

      <HelpChatbot
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        onEscalate={(context) => { setEscalateContext(context); setHelpOpen(false); setSupportOpen(true); }}
      />
      <SupportTicketModal
        isOpen={supportOpen}
        onClose={() => setSupportOpen(false)}
        previousMessage={escalateContext.message}
        aiSessionId={escalateContext.sessionId}
      />
    </>
  );
};
